import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Grid,
  Typography,
  Checkbox,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
  Paper,
  Divider,
  Button,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Search, DirectionsCar, NoPhotography, VpnKey, CheckCircle, Warning, PriorityHigh } from '@mui/icons-material';
import { Vehicle } from '@otw/models/inventory';
import { VehicleNeeds, TransporterPackageStatus } from '@otw/models';
import { getVehiclePlaceholder, getVehicleColor } from '../VehicleUtils';
import { useInventory } from 'hooks/inventory/useInventory';
import { VehicleCard } from '../cards';
import ManagePhotosDialog from '@/components/inventory/dialogs/ManagePhotosDialog';

interface VehicleSelectorProps {
  selectedVehicles: Vehicle[];
  onVehicleSelectionChange: (vehicles: Vehicle[]) => void;
  vehiclePriorities?: { [vehicleId: string]: boolean };
  onVehiclePriorityChange?: (vehicleId: string, isHighPriority: boolean) => void;
  vehicleReady?: { [vehicleId: string]: { is_ready: boolean; ready_date: string } };
  onVehicleReadyChange?: (vehicleId: string, isReady: boolean, readyDate?: string) => void;
  vehicleNeeds?: { [vehicleId: string]: VehicleNeeds };
  onVehicleNeedsChange?: (vehicleId: string, needs: VehicleNeeds) => void;
  maxHeight?: number | string;
  getVehiclePackageInfo?: (vehicleId: string) => { isInPackage: boolean; packageName?: string; packageId?: string; packageType?: 'customer' | 'optic'; packageStatus?: TransporterPackageStatus };
  suppressExistingPackageWarning?: boolean;
  showSelectedVehiclesWithoutSearch?: boolean;
  /** Vehicle IDs that cannot be selected (e.g. already in transport fleet). */
  disabledVehicleIds?: string[];
  hideSelectedVehiclesSummary?: boolean;
}


const VehicleSelector: React.FC<VehicleSelectorProps> = ({
  selectedVehicles,
  onVehicleSelectionChange,
  vehiclePriorities = {},
  onVehiclePriorityChange,
  vehicleReady = {},
  onVehicleReadyChange,
  vehicleNeeds = {},
  onVehicleNeedsChange,
  maxHeight = 500,
  getVehiclePackageInfo,
  suppressExistingPackageWarning = false,
  showSelectedVehiclesWithoutSearch = false,
  disabledVehicleIds = [],
  hideSelectedVehiclesSummary = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);
  const [photoDialogVehicle, setPhotoDialogVehicle] = useState<Vehicle | null>(null);
  
  // Use the useInventory hook to fetch vehicles from master_inventory
  const { 
    vehicles = [], 
    isLoading 
  } = useInventory('master_inventory');
  
  // Filter vehicles based on search query and status
  useEffect(() => {
    // If showSelectedVehiclesWithoutSearch is true and no search query, show only selected vehicles
    if (!searchQuery.trim()) {
      if (showSelectedVehiclesWithoutSearch && selectedVehicles.length > 0) {
        // Show selected vehicles even without search
        setFilteredVehicles(selectedVehicles);
        return;
      }
      setFilteredVehicles([]);
      return;
    }
    
    const term = searchQuery.toLowerCase();
    const filtered = vehicles?.filter(
      (vehicle) => {
        // Search in all specified fields
        return (
          (vehicle.vin || '').toLowerCase().includes(term) ||
          (vehicle.make || '').toLowerCase().includes(term) ||
          (vehicle.model || '').toLowerCase().includes(term) ||
          (vehicle.year?.toString() || '').includes(term) ||
          (vehicle.who || '').toLowerCase().includes(term) ||
          (vehicle.stock_number || '').toLowerCase().includes(term)
        );
      }
    ) || [];
    
    setFilteredVehicles(filtered);
  }, [searchQuery, vehicles, selectedVehicles, showSelectedVehiclesWithoutSearch]);
  
  // Handle search submission
  const handleSearch = () => {
    setSearchQuery(searchTerm);
  };

  // Handle enter key press
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Toggle vehicle selection
  const handleVehicleToggle = (vehicle: Vehicle) => {
    if (disabledVehicleIds.includes(vehicle.id)) return;
    const isSelected = selectedVehicles.some((v) => v.id === vehicle.id);
    
    if (isSelected) {
      onVehicleSelectionChange(selectedVehicles.filter((v) => v.id !== vehicle.id));
    } else {
      onVehicleSelectionChange([...selectedVehicles, vehicle]);
    }
  };

  // Handle priority toggle
  const handlePriorityToggle = (vehicleId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation(); // Prevent vehicle selection when clicking priority toggle
    if (onVehiclePriorityChange) {
      onVehiclePriorityChange(vehicleId, event.target.checked);
    }
  };

  // Handle ready toggle
  const handleReadyToggle = (vehicleId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation(); // Prevent vehicle selection when clicking ready toggle
    if (onVehicleReadyChange) {
      const isReady = event.target.checked;
      // Set today's date if marking as ready, otherwise use 'TBD'
      const readyDate = isReady ? new Date().toISOString().split('T')[0] : 'TBD';
      onVehicleReadyChange(vehicleId, isReady, readyDate);
    }
  };

  // Handle needs toggle
  const handleNeedsToggle = (vehicleId: string, needType: 'needs_cameras' | 'needs_backup_sensors' | 'needs_anti_theft', event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation(); // Prevent vehicle selection when clicking needs toggle
    if (onVehicleNeedsChange) {
      const currentNeeds = vehicleNeeds[vehicleId] || { needs_cameras: false, needs_backup_sensors: false, needs_anti_theft: false };
      const updatedNeeds = {
        ...currentNeeds,
        [needType]: event.target.checked,
      };
      onVehicleNeedsChange(vehicleId, updatedNeeds);
    }
  };

  // Handle high priority toggle for installation needs
  const handleNeedsHighPriorityToggle = (vehicleId: string, priorityType: 'cameras_high_priority' | 'backup_sensors_high_priority' | 'anti_theft_high_priority') => {
    if (onVehicleNeedsChange) {
      const currentNeeds = vehicleNeeds[vehicleId] || { needs_cameras: false, needs_backup_sensors: false, needs_anti_theft: false };
      const updatedNeeds = {
        ...currentNeeds,
        [priorityType]: !(currentNeeds[priorityType] || false),
      };
      onVehicleNeedsChange(vehicleId, updatedNeeds);
    }
  };
  
  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label="Search Vehicles (VIN, Make, Model, Year, Who, Stock Number)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <IconButton
                    onClick={handleSearch}
                    color="primary"
                    sx={{ 
                      //backgroundColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        color: 'primary.dark',
                      },
                    }}
                  >
                    <Search />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      {selectedVehicles.length > 0 && !hideSelectedVehiclesSummary && (
        <Paper 
          elevation={0} 
          variant="outlined" 
          sx={{ p: 2, mb: 2, borderRadius: 1, bgcolor: 'background.default' }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Selected Vehicles: {selectedVehicles.length}
          </Typography>
          <Grid container spacing={1}>
            {selectedVehicles.map((vehicle) => {
              const packageInfo = getVehiclePackageInfo?.(vehicle.id);
              const isInPackage = packageInfo?.isInPackage || false;
              const isCompletedPackage = packageInfo?.packageStatus === 'COMPLETED';
              const isInActivePackage = isInPackage && !isCompletedPackage;
              const isHighPriority = vehiclePriorities[vehicle.id] || false;
              const isReady = vehicleReady[vehicle.id]?.is_ready || false;
              
              return (
                <Grid item key={vehicle.id}>
                  <Tooltip 
                    title={
                      isInActivePackage && packageInfo
                        ? `Already in ${packageInfo.packageType === 'optic' ? 'Optic' : 'Customer'} package: ${packageInfo.packageName || 'Unknown'}`
                        : undefined
                    }
                    disableHoverListener={!isInActivePackage}
                  >
                    <Chip
                      icon={isHighPriority ? <PriorityHigh fontSize="small" /> : <DirectionsCar fontSize="small" />}
                      label={`${vehicle.year} ${vehicle.make} ${vehicle.model}${isInActivePackage ? ` (${packageInfo?.packageType === 'optic' ? 'Optic' : packageInfo?.packageType === 'customer' ? 'Customer' : ''} Package)` : ''}${isHighPriority ? ' (HIGH PRIORITY)' : ''}${isReady ? ' (READY)' : ''}`}
                      onDelete={() => handleVehicleToggle(vehicle)}
                      color={isInActivePackage ? "warning" : isHighPriority ? "error" : isReady ? "success" : "primary"}
                      variant="outlined"
                      sx={{
                        ...(isInActivePackage && {
                          borderColor: 'warning.main',
                          '& .MuiChip-label': {
                            color: 'warning.main',
                          }
                        }),
                        ...(isHighPriority && !isInActivePackage && {
                          borderColor: 'error.main',
                          '& .MuiChip-label': {
                            color: 'error.main',
                            fontWeight: 'bold'
                          }
                        }),
                        ...(isReady && !isInActivePackage && !isHighPriority && {
                          borderColor: 'success.main',
                          '& .MuiChip-label': {
                            color: 'success.main',
                            fontWeight: 'bold'
                          }
                        })
                      }}
                    />
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
          {!suppressExistingPackageWarning && selectedVehicles.some(vehicle => {
            const packageInfo = getVehiclePackageInfo?.(vehicle.id);
            return packageInfo?.isInPackage && packageInfo?.packageStatus !== 'COMPLETED';
          }) && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
              ⚠️ Some selected vehicles are already in existing packages
              {(() => {
                const customerPackages = selectedVehicles.filter(v => {
                  const info = getVehiclePackageInfo?.(v.id);
                  return info?.packageType === 'customer' && info?.packageStatus !== 'COMPLETED';
                }).length;
                const opticPackages = selectedVehicles.filter(v => {
                  const info = getVehiclePackageInfo?.(v.id);
                  return info?.packageType === 'optic' && info?.packageStatus !== 'COMPLETED';
                }).length;
                const parts: string[] = [];
                if (customerPackages > 0) parts.push(`${customerPackages} in Customer package${customerPackages > 1 ? 's' : ''}`);
                if (opticPackages > 0) parts.push(`${opticPackages} in Optic package${opticPackages > 1 ? 's' : ''}`);
                return parts.length > 0 ? ` (${parts.join(', ')})` : '';
              })()}
            </Typography>
          )}
        </Paper>
      )}
      
      <Box 
        sx={{ 
          maxHeight, 
          overflow: 'auto', 
          pr: 1,
          pb: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
          }
        }}
      >
        {!searchQuery.trim() && !(showSelectedVehiclesWithoutSearch && selectedVehicles.length > 0) ? (
          <Box 
            display="flex" 
            flexDirection="column" 
            justifyContent="center" 
            alignItems="center" 
            height={300}
            sx={{ color: 'text.secondary' }}
          >
            <Search sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6">
              Search for vehicles
            </Typography>
            <Typography variant="body2">
              Enter a search term and press Enter or click Search to find vehicles
            </Typography>
          </Box>
        ) : isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={300}>
            <CircularProgress />
          </Box>
        ) : filteredVehicles.length === 0 ? (
          <Box 
            display="flex" 
            flexDirection="column" 
            justifyContent="center" 
            alignItems="center" 
            height={300}
            sx={{ color: 'text.secondary' }}
          >
            <NoPhotography sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6">
              No vehicles found
            </Typography>
            <Typography variant="body2">
              Try adjusting your search terms
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredVehicles.map((vehicle) => {
              const isSelected = selectedVehicles.some((v) => v.id === vehicle.id);
              const isDisabled = disabledVehicleIds.includes(vehicle.id);
              const packageInfo = getVehiclePackageInfo?.(vehicle.id);
              const isInPackage = packageInfo?.isInPackage || false;
              const isCompletedPackage = packageInfo?.packageStatus === 'COMPLETED';
              const isInActivePackage = isInPackage && !isCompletedPackage;
              const isHighPriority = vehiclePriorities[vehicle.id] || false;
              const isReady = vehicleReady[vehicle.id]?.is_ready || false;
              
              return (
                <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                  <Tooltip
                    title={isDisabled ? 'Already in transport fleet' : undefined}
                    disableHoverListener={!isDisabled}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        '&:hover': {
                          '& .vehicle-card': {
                            boxShadow: isDisabled ? 0 : 2,
                          }
                        }
                      }}
                      onClick={() => handleVehicleToggle(vehicle)}
                    >
                      <VehicleCard
                        vehicle={vehicle}
                        actions={[]}
                        onManagePhotos={(vehicle) => {
                          setPhotoDialogVehicle(vehicle);
                        }}
                        className="vehicle-card"
                        sx={{
                          border: isSelected ? '2px solid' : '1px solid',
                          borderColor: isSelected ? (isHighPriority ? 'error.main' : isReady ? 'success.main' : 'primary.main') : isInActivePackage ? 'warning.main' : 'divider',
                          position: 'relative',
                          opacity: isDisabled ? 0.6 : (isInActivePackage && !isSelected ? 0.7 : 1),
                          '&:hover': {
                            boxShadow: 2,
                          },
                        }}
                      />
                    
                    {/* Priority, Ready, and Needs toggles - only show when vehicle is selected */}
                    {isSelected && (onVehiclePriorityChange || onVehicleReadyChange || onVehicleNeedsChange) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          zIndex: 3,
                          bgcolor: 'rgba(255,255,255,0.95)',
                          borderRadius: 1,
                          p: 0.5,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          maxWidth: '200px',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Priority Toggle */}
                        {onVehiclePriorityChange && (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isHighPriority}
                                onChange={(e) => handlePriorityToggle(vehicle.id, e)}
                                size="small"
                                color="error"
                              />
                            }
                            label={
                              <Typography variant="caption" sx={{ fontWeight: 'bold', color: isHighPriority ? 'error.main' : 'text.secondary' }}>
                              High Priority
                            </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                        )}
                        
                        {/* Ready Toggle */}
                        {onVehicleReadyChange && (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isReady}
                                onChange={(e) => handleReadyToggle(vehicle.id, e)}
                                size="small"
                                color="success"
                              />
                            }
                            label={
                              <Typography variant="caption" sx={{ fontWeight: 'bold', color: isReady ? 'success.main' : 'text.secondary' }}>
                                Ready
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                        )}

                        {/* Needs Toggles */}
                        {onVehicleNeedsChange && (
                          <>
                            <Divider sx={{ my: 0.5 }} />
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', px: 0.5 }}>
                              Installation Needs:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={vehicleNeeds[vehicle.id]?.needs_cameras || false}
                                    onChange={(e) => handleNeedsToggle(vehicle.id, 'needs_cameras', e)}
                                    size="small"
                                    color="primary"
                                  />
                                }
                                label={
                                  <Typography variant="caption" sx={{ color: vehicleNeeds[vehicle.id]?.needs_cameras ? 'primary.main' : 'text.secondary' }}>
                                    Cameras
                                  </Typography>
                                }
                                sx={{ m: 0, flex: 1 }}
                              />
                              {vehicleNeeds[vehicle.id]?.needs_cameras && (
                                <Tooltip title={vehicleNeeds[vehicle.id]?.cameras_high_priority ? "High Priority - Click to remove" : "Click to mark as High Priority"}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleNeedsHighPriorityToggle(vehicle.id, 'cameras_high_priority');
                                    }}
                                    sx={{
                                      p: 0.5,
                                      minWidth: 24,
                                      minHeight: 24,
                                      color: vehicleNeeds[vehicle.id]?.cameras_high_priority ? 'error.main' : 'text.secondary',
                                      '&:hover': {
                                        color: 'error.main',
                                        bgcolor: 'error.50'
                                      }
                                    }}
                                  >
                                    <PriorityHigh sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={vehicleNeeds[vehicle.id]?.needs_backup_sensors || false}
                                    onChange={(e) => handleNeedsToggle(vehicle.id, 'needs_backup_sensors', e)}
                                    size="small"
                                    color="primary"
                                  />
                                }
                                label={
                                  <Typography variant="caption" sx={{ color: vehicleNeeds[vehicle.id]?.needs_backup_sensors ? 'primary.main' : 'text.secondary' }}>
                                    Backup Sensors
                                  </Typography>
                                }
                                sx={{ m: 0, flex: 1 }}
                              />
                              {vehicleNeeds[vehicle.id]?.needs_backup_sensors && (
                                <Tooltip title={vehicleNeeds[vehicle.id]?.backup_sensors_high_priority ? "High Priority - Click to remove" : "Click to mark as High Priority"}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleNeedsHighPriorityToggle(vehicle.id, 'backup_sensors_high_priority');
                                    }}
                                    sx={{
                                      p: 0.5,
                                      minWidth: 24,
                                      minHeight: 24,
                                      color: vehicleNeeds[vehicle.id]?.backup_sensors_high_priority ? 'error.main' : 'text.secondary',
                                      '&:hover': {
                                        color: 'error.main',
                                        bgcolor: 'error.50'
                                      }
                                    }}
                                  >
                                    <PriorityHigh sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={vehicleNeeds[vehicle.id]?.needs_anti_theft || false}
                                    onChange={(e) => handleNeedsToggle(vehicle.id, 'needs_anti_theft', e)}
                                    size="small"
                                    color="primary"
                                  />
                                }
                                label={
                                  <Typography variant="caption" sx={{ color: vehicleNeeds[vehicle.id]?.needs_anti_theft ? 'primary.main' : 'text.secondary' }}>
                                    Anti-Theft
                                  </Typography>
                                }
                                sx={{ m: 0, flex: 1 }}
                              />
                              {vehicleNeeds[vehicle.id]?.needs_anti_theft && (
                                <Tooltip title={vehicleNeeds[vehicle.id]?.anti_theft_high_priority ? "High Priority - Click to remove" : "Click to mark as High Priority"}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleNeedsHighPriorityToggle(vehicle.id, 'anti_theft_high_priority');
                                    }}
                                    sx={{
                                      p: 0.5,
                                      minWidth: 24,
                                      minHeight: 24,
                                      color: vehicleNeeds[vehicle.id]?.anti_theft_high_priority ? 'error.main' : 'text.secondary',
                                      '&:hover': {
                                        color: 'error.main',
                                        bgcolor: 'error.50'
                                      }
                                    }}
                                  >
                                    <PriorityHigh sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </>
                        )}
                      </Box>
                    )}

                    {/* Package status indicator */}
                    {isInActivePackage && packageInfo && (
                      <Tooltip title={`Already in ${packageInfo.packageType === 'optic' ? 'Optic' : 'Customer'} package: ${packageInfo.packageName}`}>
                        <Chip
                          icon={<Warning />}
                          label={packageInfo.packageType === 'optic' ? 'Repair' : packageInfo.packageType === 'customer' ? 'Customer' : 'Shop'}
                          size="small"
                          color="warning"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              left: isSelected && (onVehiclePriorityChange || onVehicleReadyChange) ? 160 : 8,
                              zIndex: 2,
                              fontSize: '0.7rem',
                              height: '20px',
                            }}
                        />
                      </Tooltip>
                    )}

                    {/* High Priority indicator - only show when not selected or when no toggle is available */}
                    {isHighPriority && (!isSelected || !onVehiclePriorityChange) && (
                      <Tooltip title="High Priority Vehicle">
                        <Chip
                          icon={<PriorityHigh />}
                          label="HIGH PRIORITY"
                          size="small"
                          color="error"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: isInActivePackage ? 100 : 8,
                            zIndex: 2,
                            fontSize: '0.7rem',
                            height: '20px',
                            fontWeight: 'bold'
                          }}
                        />
                      </Tooltip>
                    )}

                    {/* Ready indicator - only show when not selected or when no toggle is available */}
                    {isReady && (!isSelected || !onVehicleReadyChange) && (
                      <Tooltip title="Ready for Pickup">
                        <Chip
                          label="READY"
                          size="small"
                          color="success"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: isInActivePackage ? (isHighPriority ? 200 : 100) : (isHighPriority ? 100 : 8),
                            zIndex: 2,
                            fontSize: '0.7rem',
                            height: '20px',
                            fontWeight: 'bold'
                          }}
                        />
                      </Tooltip>
                    )}
                    
                    <Checkbox
                      checked={isSelected}
                      disabled={isDisabled}
                      sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        right: 8,
                        bgcolor: 'rgba(255,255,255,0.8)',
                        borderRadius: '50%',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        color: isSelected ? (isHighPriority ? 'error.main' : isReady ? 'success.main' : 'primary.main') : 'action.active',
                        zIndex: 2,
                      }}
                    />

                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          bgcolor: isHighPriority ? 'rgba(211, 47, 47, 0.08)' : isReady ? 'rgba(76, 175, 80, 0.08)' : 'rgba(25, 118, 210, 0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                          zIndex: 1,
                        }}
                      >
                        <CheckCircle 
                          color={isHighPriority ? "error" : isReady ? "success" : "primary"}
                          sx={{ 
                            fontSize: 48,
                            opacity: 0.8,
                            filter: 'drop-shadow(0px 0px 3px rgba(255,255,255,0.7))'
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
      
      {/* Manage Photos Dialog */}
      {photoDialogVehicle && (
        <ManagePhotosDialog
          collectionName="master_inventory"
          open={!!photoDialogVehicle}
          onClose={() => setPhotoDialogVehicle(null)}
          vehicle={photoDialogVehicle}
        />
      )}
    </Box>
  );
};

export default VehicleSelector; 